import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Convert, Expense } from "@/models/responses";
import { ColDef } from "ag-grid-community";
import { dateFormatter, euroFormatterTwoDecimals, paramsEuroFormatter } from "@/utils/formatters";
import { utcDateFilterParams } from "@/utils/grid";
import Grid, { GridDropdownFilter } from "@/lib/organisms/Grid";
import { errorPopup, successPopup } from "@/utils/alerts";
import Swal from "sweetalert2";
import { useAuth } from "@/hooks/useAuth";
import { PeriodName } from "@/models";
import FileUploadDialog from "@/lib/shadcn/components/file-upload-dialog";
import { decodeErrorResponse } from "@/utils/errors";
import { useAppData } from "@/hooks/useAppData";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { UploadIcon } from "@radix-ui/react-icons";
import { useAllApartmentEntities, useAllApartmentOptions } from "@/store/slices/apartments";
import { fetchOptions } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";

interface ExpenseRow {
    id: number;
    /**
     * Numeric apartment ID retained on the row so cross-cutting filters
     * (the shared `<ApartmentFilterBar />` predicates) can resolve the row
     * back to its `Apartment` record without re-reading the source `Expense`
     * list. The visible `apartment` field carries the human-readable name.
     */
    apartmentIdInternal: number;
    apartment: string;
    expenseCategory: string;
    date: Date;
    amount: number;
    notes: string;
}

const ExpensesPage: React.FC = () => {
    const { user } = useAuth();
    const [expenses, setExpenses] = useState<ExpenseRow[]>([]);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const gridName = "Expenses";

    const { yearOptions, periodOptions, selectedPeriod, selectedYear, selectedOwner, ownerOptions, expenseCategoriesMap } = useAppData();

    const apartmentEntities = useAllApartmentEntities();
    const apartmentOptions = useAllApartmentOptions();

    const { predicates } = useApartmentFilters();

    /**
     * Page-local apartment selection (0 == "All"). Deliberately NOT the global
     * `userSettings.selectedApartment`: that value is force-set to the first
     * apartment whenever apartments load (auto-select-first), so it can never
     * express "show every apartment". A local picker lets this table default to
     * the whole portfolio and filter on demand.
     */
    const [apartmentFilter, setApartmentFilter] = useState(0);

    /**
     * Apply the shared apartment-filter predicates to the loaded expense rows.
     * Each row carries `apartmentIdInternal`; we resolve via `apartmentEntities`
     * and feed every active predicate. Rows whose apartment is unknown are
     * excluded under any active filter — same semantics as Tier 1.
     */
    const filteredExpenses = useMemo(() => {
        // The "Apartment" dropdown narrows to a single apartment (0 == "All").
        const byApartment =
            apartmentFilter !== 0
                ? expenses.filter((row) => row.apartmentIdInternal === apartmentFilter)
                : expenses;

        if (predicates.length === 0) {
            return byApartment;
        }
        return byApartment.filter((row) => {
            const apartment = apartmentEntities[row.apartmentIdInternal];
            if (!apartment) {
                return false;
            }
            return predicates.every((predicate) => predicate(apartment));
        });
    }, [expenses, predicates, apartmentEntities, apartmentFilter]);

    const { setSelectedYear, setSelectedPeriod, setSelectedOwner } = useUserSettingsDispatch();

    const handleFileUpload = (file: File) => {
        const formData = new FormData();
        formData.append("file", file);
        void Swal.fire({
            title: "Uploading file...",
            allowOutsideClick: false,
        });
        Swal.showLoading();
        fetch(`${apiUrl}/api/admin/expenses/upload`, {
            method: "POST",
            body: formData,
            credentials: "include",
        })
            .then((response) => {
                if (!response.ok) {
                    return decodeErrorResponse(response);
                }
                return response.text();
            })
            .then((response) => {
                const importResult = Convert.toImportExpensesResponse(response).importExpensesResult;
                successPopup(
                    `Successfully processed expenses file\n New items: ${importResult.newExpenseItems} expenses, Deleted items: ${importResult.DeletedExpenseItems}`,
                );
            })
            .catch((error) => {
                Swal.close();
                void errorPopup(error.message);
            });
    };
    const getAndSetExpenses = useCallback(
        (year: number, period: string, ownerId: number): void => {
            // Admins load every apartment's expenses and narrow client-side via the
            // Apartment dropdown / filter bar; owners get their own pre-scoped set.
            const url = `${apiUrl}/api${user?.isAdmin ? "/admin" : "/owners/" + ownerId}/expenses?year=${year}&period=${period}`;
            fetch(url, fetchOptions)
                .then((response) => {
                    if (response) {
                        return response.text();
                    }
                    throw new Error("Unauthorized");
                })
                .then((response) => {
                    let responseJson;
                    try {
                        responseJson = Convert.toGetExpensesResponse(response);
                    } catch (e) {
                        console.error("Invalid JSON string:", response);
                        throw e;
                    }
                    setExpenses(
                        responseJson.expenses.map((expense: Expense) => {
                            return {
                                id: expense.id,
                                apartmentIdInternal: expense.apartmentId,
                                apartment: apartmentEntities[expense.apartmentId]?.name || "N/A",
                                expenseCategory: expenseCategoriesMap.get(expense.categoryId)?.category || "N/A",
                                date: new Date(expense.date),
                                amount: expense.amount,
                                notes: expense.notes,
                            };
                        }),
                    );
                })
                .catch((error) => console.log(error));
        },
        [apartmentEntities, expenseCategoriesMap, user?.isAdmin],
    );

    const amountRow: ColDef<ExpenseRow> = {
        field: "amount",
        headerName: "Amount",
        valueFormatter: paramsEuroFormatter,
        cellDataType: "number",
        cellClass: "currencyFormat",
        width: 140,
        filter: "agNumberColumnFilter",
        filterValueGetter: (value) => {
            if (value.data !== undefined) {
                return Math.round(value.data.amount);
            }
        },
    };
    const columnDefinitions: ColDef[] = [
        {
            field: "apartment",
            headerName: "Apartment",
            cellDataType: "text",
            filter: "agTextColumnFilter",
            width: 290,
        },
        {
            field: "expenseCategory",
            headerName: "Category",
            cellDataType: "text",
            filter: "agSetColumnFilter",
            width: 220,
        },
        {
            field: "date",
            headerName: "Date",
            valueFormatter: dateFormatter,
            cellDataType: "date",
            filter: "agDateColumnFilter",
            filterParams: utcDateFilterParams,
            width: 210,
        },
        amountRow,

        {
            field: "notes",
            headerName: "Notes",
            flex: 1,
            cellDataType: "text",
            filter: "agTextColumnFilter",
        },
    ];

    useEffect(() => {
        getAndSetExpenses(selectedYear, selectedPeriod, selectedOwner);
    }, [selectedPeriod, selectedYear, apartmentEntities, expenseCategoriesMap, selectedOwner, getAndSetExpenses]);

    const gridDropdownFilters: GridDropdownFilter[] = [
        {
            label: "Period",
            options: periodOptions,
            selectedOption: selectedPeriod,
            onSelectionChanged: (period) => setSelectedPeriod(period as PeriodName),
        },
        {
            label: "Year",
            options: yearOptions,
            selectedOption: selectedYear.toString(),
            onSelectionChanged: (year) => setSelectedYear(Number(year)),
        },
    ];

    if (user?.isAdmin) {
        gridDropdownFilters.push({
            label: "Apartment",
            testId: "expenses-filter-apartment",
            options: apartmentOptions,
            selectedOption: apartmentFilter.toString(),
            onSelectionChanged: (apartment) => setApartmentFilter(Number(apartment)),
        });
    } else {
        gridDropdownFilters.push({
            label: "Owner",
            options: ownerOptions,
            selectedOption: selectedOwner.toString(),
            onSelectionChanged: (owner) => setSelectedOwner(Number(owner)),
        });
    }

    const importExpensesButton = {
        label: "Import",
        onClick: () => setShowUploadModal(true),
        icon: UploadIcon,
    };
    return (
        <div data-testid="apartment-expenses-page" className="flex flex-col gap-2">
            <ApartmentFilterBar />
            <FileUploadDialog open={showUploadModal} onClose={() => setShowUploadModal(false)} onUpload={handleFileUpload} />
            <Grid<ExpenseRow>
                columnDefs={columnDefinitions}
                gridName={gridName}
                initialRowData={filteredExpenses}
                buttons={user?.isAdmin ? [importExpensesButton] : []}
                dropdownFilters={gridDropdownFilters}
                getRowId={(params) => params.data.id.toString()}
                columnsToAggregate={[
                    { colDef: amountRow, formatter: euroFormatterTwoDecimals },
                    {
                        colDef: amountRow,
                        formatter: euroFormatterTwoDecimals,
                        aggregateFunction: "avg",
                    },
                ]}
            />
        </div>
    );
};

export default ExpensesPage;
